(() => {
  'use strict';

  const STORAGE_KEY = 'vs_attribution_v1';
  const COOKIE_KEY = 'vs_attribution_v1';
  const MAX_AGE_DAYS = 90;
  const MAX_FIELD_LENGTH = 950;

  const UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_id',
  ];

  const CLICK_ID_KEYS = [
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'ttclid',
    'msclkid',
  ];

  const TRACKED_KEYS = UTM_KEYS.concat(CLICK_ID_KEYS);

  const nowIso = () => new Date().toISOString();

  const safeJsonParse = (value) => {
    try {
      return value ? JSON.parse(value) : null;
    } catch (_error) {
      return null;
    }
  };

  const readCookie = (name) => {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  };

  const writeCookie = (name, value) => {
    const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
    document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; SameSite=Lax`;
  };

  const readStored = () => {
    const localValue = (() => {
      try {
        return window.localStorage.getItem(STORAGE_KEY);
      } catch (_error) {
        return null;
      }
    })();
    return safeJsonParse(localValue) || safeJsonParse(readCookie(COOKIE_KEY)) || {};
  };

  const writeStored = (payload) => {
    const value = JSON.stringify(payload);
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (_error) {
      // Storage can be blocked. The first-party cookie fallback below still keeps attribution usable.
    }
    writeCookie(COOKIE_KEY, value);
  };

  const getParams = () => {
    const params = new URLSearchParams(window.location.search);
    const out = {};
    TRACKED_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) out[key] = value.trim().slice(0, 300);
    });
    return out;
  };

  const hostnameFromUrl = (url) => {
    try {
      return new URL(url).hostname.replace(/^www\./, '').toLowerCase();
    } catch (_error) {
      return '';
    }
  };

  const isInternalReferrer = (referrerHost) => {
    const currentHost = window.location.hostname.replace(/^www\./, '').toLowerCase();
    return referrerHost && (referrerHost === currentHost || referrerHost.endsWith(`.${currentHost}`));
  };

  const trafficFromReferrer = (referrerHost) => {
    if (!referrerHost) return null;

    if (/google\./.test(referrerHost)) return { source: 'google', medium: 'organic_search' };
    if (/bing\./.test(referrerHost)) return { source: 'bing', medium: 'organic_search' };
    if (/yahoo\./.test(referrerHost)) return { source: 'yahoo', medium: 'organic_search' };
    if (/duckduckgo\./.test(referrerHost)) return { source: 'duckduckgo', medium: 'organic_search' };

    if (/(^|\.)instagram\.com$/.test(referrerHost) || referrerHost === 'l.instagram.com') {
      return { source: 'instagram', medium: 'organic_social' };
    }
    if (/(^|\.)facebook\.com$/.test(referrerHost) || referrerHost === 'l.facebook.com') {
      return { source: 'facebook', medium: 'organic_social' };
    }
    if (/(^|\.)tiktok\.com$/.test(referrerHost)) return { source: 'tiktok', medium: 'organic_social' };
    if (/(^|\.)pinterest\./.test(referrerHost)) return { source: 'pinterest', medium: 'organic_social' };
    if (/(^|\.)youtube\.com$/.test(referrerHost) || referrerHost === 'youtu.be') {
      return { source: 'youtube', medium: 'organic_social' };
    }

    return { source: referrerHost, medium: 'referral' };
  };

  const clickIdType = (params) => CLICK_ID_KEYS.find((key) => params[key]) || '';

  const buildTouch = (params, stored) => {
    const referrer = document.referrer || '';
    const referrerHost = hostnameFromUrl(referrer);
    const hasTrackedParam = Object.keys(params).length > 0;
    const hasExternalReferrer = referrerHost && !isInternalReferrer(referrerHost);
    const clickType = clickIdType(params);

    let source = params.utm_source || '';
    let medium = params.utm_medium || '';

    if (!source || !medium) {
      if (params.gclid || params.gbraid || params.wbraid) {
        source = source || 'google';
        medium = medium || 'paid_search';
      } else if (params.ttclid) {
        source = source || 'tiktok';
        medium = medium || 'paid_social';
      } else if (params.fbclid) {
        source = source || 'meta';
        medium = medium || 'paid_social';
      } else if (hasExternalReferrer) {
        const inferred = trafficFromReferrer(referrerHost);
        source = source || inferred.source;
        medium = medium || inferred.medium;
      }
    }

    if (!source && !stored.first_touch) {
      source = 'direct';
      medium = 'none';
    }

    if (!source) return null;

    return {
      at: nowIso(),
      source,
      medium,
      campaign: params.utm_campaign || '',
      content: params.utm_content || '',
      term: params.utm_term || '',
      utm_id: params.utm_id || '',
      click_id_type: clickType,
      click_id: clickType ? params[clickType] : '',
      landing_page: window.location.href.slice(0, 700),
      referrer: hasExternalReferrer ? referrer.slice(0, 700) : '',
    };
  };

  const compact = (value) => JSON.stringify(value || {}).slice(0, MAX_FIELD_LENGTH);

  const buildCartAttributes = (stored) => {
    const first = stored.first_touch || {};
    const firstMarketing = stored.first_marketing_touch || {};
    const last = stored.last_touch || first;
    const clickIds = {};
    CLICK_ID_KEYS.forEach((key) => {
      if (stored.click_ids && stored.click_ids[key]) clickIds[key] = stored.click_ids[key];
    });

    return {
      vs_first_touch: compact(first),
      vs_first_marketing_touch: compact(firstMarketing),
      vs_last_touch: compact(last),
      vs_click_ids: compact(clickIds),
      vs_attr_updated_at: nowIso(),
      vs_first_source: first.source || '',
      vs_first_medium: first.medium || '',
      vs_first_campaign: first.campaign || '',
      vs_first_marketing_source: firstMarketing.source || '',
      vs_first_marketing_medium: firstMarketing.medium || '',
      vs_first_marketing_campaign: firstMarketing.campaign || '',
      vs_last_source: last.source || '',
      vs_last_medium: last.medium || '',
      vs_last_campaign: last.campaign || '',
    };
  };

  const updateCartAttributes = (stored, keepalive = false) => {
    if (!stored || (!stored.first_touch && !stored.last_touch)) return Promise.resolve(false);

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      keepalive,
      body: JSON.stringify({ attributes: buildCartAttributes(stored) }),
    }).then(
      () => true,
      () => false
    );
  };

  const params = getParams();
  const stored = readStored();
  stored.click_ids = stored.click_ids || {};

  CLICK_ID_KEYS.forEach((key) => {
    if (params[key]) stored.click_ids[key] = params[key];
  });

  const touch = buildTouch(params, stored);
  const isDirectTouch = touch && touch.source === 'direct' && touch.medium === 'none';
  let changed = false;

  if (touch && !stored.first_touch) {
    stored.first_touch = touch;
    changed = true;
  }

  if (touch && !isDirectTouch && !stored.first_marketing_touch) {
    stored.first_marketing_touch = touch;
    changed = true;
  } else if (!stored.first_marketing_touch && stored.last_touch && !(stored.last_touch.source === 'direct' && stored.last_touch.medium === 'none')) {
    stored.first_marketing_touch = stored.last_touch;
    changed = true;
  }

  if (touch && !isDirectTouch) {
    stored.last_touch = touch;
    changed = true;
  } else if (touch && !stored.last_touch) {
    stored.last_touch = touch;
    changed = true;
  }

  if (Object.keys(params).length > 0) {
    stored.last_params = params;
    changed = true;
  }

  if (changed) writeStored(stored);

  window.VSAttribution = {
    get: () => readStored(),
    syncCart: () => updateCartAttributes(readStored(), false),
    cartAttributes: () => buildCartAttributes(readStored()),
  };

  const sync = () => updateCartAttributes(readStored(), false);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync, { once: true });
  } else {
    sync();
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = (form.getAttribute('action') || '').toLowerCase();
    const isCartOrCheckout = action.includes('/cart/add') || action.includes('/checkout') || form.querySelector('[name="checkout"]');
    if (isCartOrCheckout) updateCartAttributes(readStored(), true);
  }, true);

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('a, button, input[type="submit"]') : null;
    if (!target) return;
    const text = (target.textContent || target.value || '').toLowerCase();
    const href = target instanceof HTMLAnchorElement ? target.href.toLowerCase() : '';
    const name = (target.getAttribute('name') || '').toLowerCase();
    if (href.includes('/checkout') || name === 'checkout' || /checkout|kasse|bezahlen/.test(text)) {
      updateCartAttributes(readStored(), true);
    }
  }, true);
})();
